"use client";

import { Stagger, StaggerItem } from "@aio/platform-ui";
import * as React from "react";

export interface DashboardPageProps {
  title: string;
  description?: string;
  dateRangePicker?: React.ReactNode;
  /** Widgets — caller composes with Card or custom blocks */
  children: React.ReactNode;
}

// Each top-level child is wrapped in a StaggerItem so the dashboard's grid
// fades in one card at a time on mount. This is the platform-default for
// any DashboardPage — callers don't need to know about animation; they just
// drop their widgets in as `children` and get the choreography for free.
function wrapChildrenAsStaggerItems(children: React.ReactNode): React.ReactNode {
  return React.Children.map(children, (child, index) => {
    if (!React.isValidElement(child)) return child;
    return <StaggerItem key={index}>{child}</StaggerItem>;
  });
}

export function DashboardPage({
  title,
  description,
  dateRangePicker,
  children,
}: DashboardPageProps) {
  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          {description ? <p className="mt-1 text-sm text-fg-muted">{description}</p> : null}
        </div>
        {dateRangePicker}
      </header>
      <Stagger className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {wrapChildrenAsStaggerItems(children)}
      </Stagger>
    </div>
  );
}
